import { useEffect, useMemo, useState } from 'react';
import {
  createShoppingCartsClient,
  removeAnonymousTokenHeader,
  updateClientAccessToken,
  updateClientLanguage,
  updateClientPlatform,
} from '../helpers/client';
import { assignCustomerDetails } from '../helpers/customerHelper';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { RootState } from '../redux/store';
import { clearCustomer } from '../redux/reducers/customerReducer';
import tokenRefresher from '../helpers/tokenRefresher';
import { login } from '../redux/reducers/authReducer';
import { loadShortDataLinks } from '../redux/reducers/linksReducer';
import { loadCategories } from '../redux/reducers/categoriesReducer';
import { loadSettings } from '../redux/reducers/settingsReducer';
import { trackersRunner } from '../integrations/trackersRunner';
import { loadCartAsync } from '../redux/reducers/shoppingCartReducer';
import { TokenStorage } from '../helpers/tokenStorage';

const AuthProvider = ({ children }: { children: React.JSX.Element }) => {
  const dispatch = useAppDispatch();
  const cartClient = useMemo(() => createShoppingCartsClient(), []);
  const [isUserInfoLoaded, setIsUserInfoLoaded] = useState(false);
  const isUserLoggedIn = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const accessToken = useAppSelector((state: RootState) => state.authState.accessToken);
  const customerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const linksLoaded = useAppSelector((state: RootState) => state.linksState.loaded);
  const categoriesLoaded = useAppSelector((state: RootState) => state.categoriesState.loaded);
  const settingsLoaded = useAppSelector((state: RootState) => state.settingsState.loaded);
  const cartItems = useAppSelector((state: RootState) => state.shoppingCartState.items);

  tokenRefresher.setupInterceptors();

  useEffect(() => {
    dispatch(loadShortDataLinks());
    updateClientLanguage();
    updateClientPlatform();
  }, [dispatch]);

  useEffect(() => {
    if (!categoriesLoaded) {
      dispatch(loadCategories());
    }
  }, [categoriesLoaded, dispatch]);

  useEffect(() => {
    if (!settingsLoaded) {
      dispatch(loadSettings());
    }
  }, [dispatch, settingsLoaded]);

  useEffect(() => {
    if (accessToken) {
      updateClientAccessToken(accessToken);
      assignCustomerDetails()
        .then(() => {
          setIsUserInfoLoaded(true);
        })
        .catch((err) => {
          console.log(err);
          setIsUserInfoLoaded(true);
        });
    } else {
      updateClientAccessToken();
      setIsUserInfoLoaded(true);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!isUserLoggedIn) {
      if (customerInfo.id) {
        dispatch(clearCustomer());
      }
      dispatch(loadCartAsync());
    } else {
      loadCartForLoggedInUserAsync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedIn]);

  useEffect(() => {
    if (customerInfo.id && !isUserLoggedIn) {
      dispatch(login());
    }
    if (customerInfo.id) {
      trackersRunner.identifyContact();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerInfo]);

  const isLoaded = () => isUserInfoLoaded && linksLoaded && categoriesLoaded && settingsLoaded;

  const loadCartForLoggedInUserAsync = async () => {
    // Move cart if not empty
    if (cartItems?.length > 0) {
      try {
        await cartClient.moveToCustomer();
      } catch (ex) {
        console.error('Not possible to move cart', ex);
      }
    }

    removeAnonymousTokenHeader();
    TokenStorage.Instance.clearAnonymousToken();
    dispatch(loadCartAsync());
  };

  return isLoaded() && children;
};

export default AuthProvider;
